<script setup>
import { storeToRefs } from 'pinia'
import SkeletonPlaceholder from '@/components/Base/SkeletonPlaceholder.vue'
import { useMessageStore } from '~/stores/messages'
import { useParkingStore } from '~/stores/parking'
import { useAuthStore } from '~/stores/auth'
import { signIn } from '~/composables/useMsal'
import { PARKING_CONSTANTS } from '@/utils/parkingConstants'

// Fetch route
const route = useRoute()
const centerRoute = computed(() => route.params?.centerUid)

// Fetch data
const { data: centers, suspense: centerSuspense } = useFetchCenterList()

// Prefetch data on the server side
onServerPrefetch(async () => {
    await centerSuspense() // Wait for the query to finish fetching
})

// Fetch messages
const messagesStore = useMessageStore()
const { messages: staticKeys } = storeToRefs(messagesStore)

const localePath = useLocalePath()

// Fetch parking store
const parkingStore = useParkingStore()
const { paymentProgress } = storeToRefs(parkingStore)
const { updateTicketPaymentProgress } = parkingStore

// Fetch auth store
const authStore = useAuthStore()
const { isLoggedIn } = storeToRefs(authStore)

// Get Webview global state
const isWebview = useState('state_isWebview')

// Compute payment headline, depending on payment progress
const getParkingPaymentHeadline = computed(() => {
    const headlines = {
        0: staticKeys.value?.parking_ticketpayment_title,
        1: staticKeys.value?.parking_ticketpayment_title,
        2: staticKeys.value?.parking_ticketpayment_title
    }

    return headlines[paymentProgress.value]
})

// Compute progress bar labels, depending on payment progress
const getParkingPaymentLabels = computed(() => {
    return [
        staticKeys.value?.parking_progress_step1,
        staticKeys.value?.parking_progress_step2,
        staticKeys.value?.parking_progress_step3
    ]
})

// Compute percentage
const getPercentage = computed(() => {
    const sections = getParkingPaymentLabels.value.length - 1
    return paymentProgress.value > 0 ? `${(100 / sections) * paymentProgress.value}%` : '0%'
})

// Compute progress bar config
const progressBarConfig = computed(() => {
    return {
        progress: paymentProgress.value,
        labels: getParkingPaymentLabels.value,
        action: true,
        showLabel: true,
        percentage: getPercentage.value
    }
})

/**
 * Handle click on progress bar and update payment progress if index is valid.
 *
 * @param {number} index - The index of the progress bar clicked.
 * @return {boolean} Returns false if index is greater than current progress, otherwise updates the payment progress.
 */
const handleProgressBarClick = (index) => {
    if (index > paymentProgress.value) {
        return false
    }

    updateTicketPaymentProgress(index)
}

/**
 * Function to handle click on center selection.
 *
 * @param {type} value - the value used for navigation
 * @return {Promise<void>} Promise representing the completion of the navigation
 */
const handleCenterSelectionClick = async (value) => {
    await navigateTo(localePath({
        name: 'centerUid-parking-payTicket',
        params: { centerUid: value }
    }))
}

// Dynamic components
const components = {
    0: resolveComponent('ParkingPaymentEnterTicketForm'),
    1: resolveComponent('ParkingPaymentVerifyTicketForm'),
    2: resolveComponent('ParkingPaymentPayTicketForm')
}

// Set dynamic component in sidebar, depending on login status
const parkingPaymentComponents = computed(() => components[paymentProgress.value])

const parkingCentersArr = computed(() => {
    return centers.value
        .filter(center => center.productTypes?.includes(PARKING_CONSTANTS.PARKING_TICKET)) // Keep only centers with 'ParkingTicket'
        .map(({ uid, name }) => ({
            value: uid,
            label: name
        }))
})

const centerName = computed(() => {
    const center = centers.value.find(center => center.uid === centerRoute.value)
    return center ? center.name : ''
})

// Compute center selection config
const getSimpleSelectConfig = computed(() => {
    return {
        items: parkingCentersArr.value,
        selected: centerRoute.value,
        mobileRendering: false // true
    }
})

// Trigger auto login if not logged in, page is not webview and if on client
if (!isLoggedIn.value && import.meta.client && !isWebview.value) {
    await signIn({
        context: 'parking-payment',
        state: 'ignoreOnBoarding', // Maybe not necessary due to meta in page
        center: centerRoute.value,
        centerName: centerName.value
    })
}
</script>

<template>
    <ContentMainHeadline :content-main-headline-config="{ title: getParkingPaymentHeadline }" />

    <template v-if="isLoggedIn">
        <ProgressBar
            :progress-bar-config="progressBarConfig"
            @update:current-index="handleProgressBarClick"
        />

        <SimpleSelect
            v-if="paymentProgress < 1"
            :simple-select-config="getSimpleSelectConfig"
            @update:current-value="handleCenterSelectionClick"
        />

        <component :is="parkingPaymentComponents" />
    </template>

    <div v-else>
        <component :is="SkeletonPlaceholder" :skeleton-placeholder-config="{ type: 'multi-headline' }" />
        <br>
        <br>
        <DefaultContentBox class="skeleton-placeholder-container">
            <component :is="SkeletonPlaceholder" :skeleton-placeholder-config="{ type: 'button' }" />
            <br>
            <br>
            <br>
            <component :is="SkeletonPlaceholder" :skeleton-placeholder-config="{ type: 'text', iterator: 2 }" />
            <br>
            <br>
            <br>
            <component :is="SkeletonPlaceholder" :skeleton-placeholder-config="{ type: 'button' }" />
        </DefaultContentBox>
    </div>

    <DevOnly><pre>{{ centers }}</pre></DevOnly>
</template>
